import React, {useEffect, useState} from "react";
import {Card, Button} from "react-bootstrap";
import {collection, getDocs, query, where, Timestamp} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import {Session} from "../../types/Types";
import TimeSeriesGraph from "./TimeSeriesGraph";

interface HourlyData {
    hour: string;
    count: number;
    timestamp: Date;
}

function SessionGraphCard() {
    const [hourlySessionData, setHourlySessionData] = useState<HourlyData[]>([]);
    const [sessionGraphLoading, setSessionGraphLoading] = useState<boolean>(true);
    const [sessionTimeScale, setSessionTimeScale] = useState<'day' | 'week'>('day');

    useEffect(() => {
        (async () => {
            await loadSessionGraph();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionTimeScale]);

    const loadSessionGraph = async () => {
        try {
            setSessionGraphLoading(true);
            
            const now = new Date();
            let cutoffDate: Date;
            let buckets: Map<string, number>;
            
            if (sessionTimeScale === 'day') {
                // 24 hours ago
                cutoffDate = new Date(now);
                cutoffDate.setHours(now.getHours() - 24);
                
                // Initialize hourly buckets
                buckets = new Map<string, number>();
                for (let i = 23; i >= 0; i--) {
                    const hourDate = new Date(now);
                    hourDate.setHours(now.getHours() - i, 0, 0, 0);
                    buckets.set(hourDate.toISOString(), 0);
                }
            } else {
                // 7 days ago
                cutoffDate = new Date(now);
                cutoffDate.setDate(now.getDate() - 7);
                
                // Initialize daily buckets
                buckets = new Map<string, number>();
                for (let i = 6; i >= 0; i--) {
                    const dayDate = new Date(now);
                    dayDate.setDate(now.getDate() - i);
                    dayDate.setHours(0, 0, 0, 0);
                    buckets.set(dayDate.toISOString(), 0);
                }
            }
            
            const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

            // Query sessions where checkInTime is within the timeframe
            const sessionsQuery = query(
                collection(db, 'sessions'),
                where('checkInTime', '>=', cutoffTimestamp)
            );

            const querySnapshot = await getDocs(sessionsQuery);
            const sessionTimes: Date[] = [];
            
            querySnapshot.forEach((doc) => {
                const sessionData = doc.data() as Session;
                if (sessionData.checkInTime) {
                    const checkInAny = sessionData.checkInTime as any;
                    const checkIn = checkInAny.toDate ? checkInAny.toDate() : new Date(sessionData.checkInTime);
                    sessionTimes.push(checkIn);
                }
            });

            // Count sessions per bucket
            sessionTimes.forEach(sessionTime => {
                const bucketStart = new Date(sessionTime);
                if (sessionTimeScale === 'day') {
                    bucketStart.setMinutes(0, 0, 0);
                } else {
                    bucketStart.setHours(0, 0, 0, 0);
                }
                
                // Find the closest bucket
                let closestKey = bucketStart.toISOString();
                let minDiff = Infinity;
                buckets.forEach((_, mapKey) => {
                    const diff = Math.abs(new Date(mapKey).getTime() - bucketStart.getTime());
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestKey = mapKey;
                    }
                });
                
                buckets.set(closestKey, (buckets.get(closestKey) || 0) + 1);
            });

            // Convert to array and format
            const hourlyData: HourlyData[] = Array.from(buckets.entries())
                .map(([timestamp, count]) => {
                    const date = new Date(timestamp);
                    let label: string;
                    if (sessionTimeScale === 'day') {
                        label = `${date.getHours()}`;
                    } else {
                        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        label = dayNames[date.getDay()];
                    }
                    return {
                        hour: label,
                        count,
                        timestamp: date
                    };
                })
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

            console.log(`Loaded session graph data: ${hourlyData.length} documents`);
            setHourlySessionData(hourlyData);
        } catch (error) {
            console.error("Error loading session graph:", error);
        } finally {
            setSessionGraphLoading(false);
        }
    };

    const maxSessionCount = Math.max(...hourlySessionData.map(d => d.count), 1);

    return (
        <Card className={"mt-3 mb-3"}>
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div><b>Sessions Graph</b></div>
                        {!sessionGraphLoading && (
                            <small className="text-muted">Peak: {maxSessionCount} per {sessionTimeScale === 'day' ? 'hour' : 'day'}</small>
                        )}
                    </div>
                    <div>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={sessionTimeScale === 'day'}
                            onClick={() => setSessionTimeScale('day')}
                        >
                            Day
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={sessionTimeScale === 'week'}
                            onClick={() => setSessionTimeScale('week')}
                        >
                            Week
                        </Button>
                    </div>
                </div>
            </Card.Header>
            <Card.Body style={{ padding: "1em" }}>
                <TimeSeriesGraph
                    data={hourlySessionData}
                    maxCount={maxSessionCount}
                    loading={sessionGraphLoading}
                    color="#8b5cf6"
                    timeScale={sessionTimeScale}
                    dataLabel="session"
                />
            </Card.Body>
        </Card>
    );
}

export default SessionGraphCard;
